import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { useKnownSwims } from './KnownSwimsContext';

const submitDateMillis = (submitDate) => {
  if (submitDate?.toMillis) return submitDate.toMillis();
  if (submitDate?.toDate) return submitDate.toDate().getTime();
  const parsedDate = new Date(submitDate);
  return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
};

const formatSubmitDate = (submitDate) => {
  const timestamp = submitDateMillis(submitDate);
  return timestamp ? new Date(timestamp).toLocaleString() : 'Unknown submission date';
};

const CuratedSwimsDashboardPage = ({ onViewSwim }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportPendingDeleteId, setReportPendingDeleteId] = useState(null);
  const { knownSwims, loading: loadingKnownSwims, error: knownSwimsError, ensureKnownSwims } = useKnownSwims();

  useEffect(() => {
    ensureKnownSwims();
  }, [ensureKnownSwims]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const reportsSnapshot = await getDocs(collection(db, 'KnownSwimUserReports'));
        const reportData = reportsSnapshot.docs.map((report) => ({ id: report.id, ...report.data() }));
        const userIds = [...new Set(reportData.map((report) => report.userID).filter(Boolean))];
        const profiles = await Promise.all(userIds.map(async (id) => {
          const profile = await getDoc(doc(db, 'users', id, 'profile', 'profile'));
          return [id, profile.exists() ? profile.data() : null];
        }));
        const profilesById = new Map(profiles);

        setReports(reportData.map((report) => ({
          ...report,
          submitterName: profilesById.get(report.userID)?.displayName || report.userID || 'Unknown user'
        })).sort((first, second) => submitDateMillis(second.submitDate) - submitDateMillis(first.submitDate)));
      } catch (loadError) {
        console.error('Unable to load Curated Swims dashboard:', loadError);
        setError('Unable to load Curated Swims dashboard.');
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  const swimsById = new Map(knownSwims.map((swim) => [swim.id, swim]));
  const reportsWithSwims = reports.map((report) => ({
    ...report,
    swim: swimsById.get(report.curatedSwimID),
    swimName: swimsById.get(report.curatedSwimID)?.name || 'Unknown curated swim'
  }));

  const deleteReport = async (reportId) => {
    setError(null);
    try {
      await deleteDoc(doc(db, 'KnownSwimUserReports', reportId));
      setReports((current) => current.filter((report) => report.id !== reportId));
    } catch (deleteError) {
      console.error('Unable to delete Curated Swim report:', deleteError);
      setError('Unable to delete the user report.');
    } finally {
      setReportPendingDeleteId(null);
    }
  };

  if (loading || loadingKnownSwims) return <div className="known-swims-dashboard-page">Loading Curated Swims dashboard...</div>;

  return (
    <section className="known-swims-dashboard-page">
      <h2>Curated Swims Dashboard</h2>
      <div className="known-swims-dashboard-count">
        <strong>{knownSwims.length}</strong>
        <span>Curated Swims</span>
      </div>
      {(error || knownSwimsError) && <p className="error">{error || knownSwimsError}</p>}
      <h3>User reports</h3>
      {reportsWithSwims.length === 0 ? (
        <p>No user reports have been submitted.</p>
      ) : (
        <div className="curated-swims-reports">
          {reportsWithSwims.map((report) => (
            <article className="curated-swims-report" key={report.id}>
              <div>
                <h4>{report.swimName}</h4>
                <p className="curated-swims-report-meta">
                  Submitted by {report.submitterName} on {formatSubmitDate(report.submitDate)}
                </p>
                <p className="curated-swims-report-text">{report.report}</p>
              </div>
              <div className="curated-swims-report-actions">
                <button onClick={() => onViewSwim(report.swim)} disabled={!report.swim}>View map</button>
                <button
                  className={reportPendingDeleteId === report.id ? 'curated-swims-report-delete-confirm' : 'curated-swims-report-delete'}
                  onClick={() => reportPendingDeleteId === report.id
                    ? deleteReport(report.id)
                    : setReportPendingDeleteId(report.id)}
                  aria-label={reportPendingDeleteId === report.id ? 'Confirm deleting user report' : 'Delete user report'}
                >
                  {reportPendingDeleteId === report.id ? 'Confirm delete' : '🗑'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default CuratedSwimsDashboardPage;
